import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clientServices } from '../../services/clientServices';
import { useTranslation } from 'react-i18next';
import { TextField, Button, Grid2, Alert, FormControl, InputLabel, Select, MenuItem, Snackbar, Typography, Paper, Box } from '@mui/material';
import { useAuth } from '../../services/authentication/authContext';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import '../../css/Form.css';
import PageHeader from '../ui/PageHeader';
import ConfirmationDialog from '../common/ConfirmationDialog';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { jwtDecode } from 'jwt-decode';

const ClientForm = () => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [contactPersonName, setContactPersonName] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [emailId, setEmailId] = useState('');
  const [address, setAddress] = useState('');
  const [category, setCategory] = useState('ADHOC');
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [jobRates, setJobRates] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [loading, setLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const [clientId, setClientId] = useState('');
  const navigationUrl = "/home/clients";
  const navigate = useNavigate();
  const isSuperAdmin = roles.includes("SUPER_ADMIN");
  const location = useLocation();
  const client = location.state?.clientData;
  const id = clientId;

  const ADHOC_OPTIONS = [
    "Bookkeeping", "Management Accounts", "Statutory/Final Accounts", "VAT",
    "Data Entry", "Payroll", "Admin Work", "PTR", "Audit"
  ];

  const FTE_OPTIONS = ["Accountant", "Bookkeeper"];

  const getInitializedRates = (cat, existingRates = []) => {
    const options = cat === 'FTE' ? FTE_OPTIONS : ADHOC_OPTIONS;

    return options.map(opt => {
      const found = existingRates.find(r => r.jobType === opt);
      return {
        jobType: opt,
        rate: found ? found.rate : 0
      };
    });
  };

  useEffect(() => {
    if (client) {
      setClientId(client.id);
    } else {
      setJobRates(getInitializedRates('ADHOC', []));
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (accessToken) {
      const decodedToken = jwtDecode(accessToken);
      let extractedRoles = [];

      if (Array.isArray(decodedToken.roles)) {
        extractedRoles = decodedToken.roles;
      } else if (typeof decodedToken.roles === "string") {
        extractedRoles = decodedToken.roles.split(",").map(role => role.trim());
      }

      setRoles(extractedRoles);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken) {
      if (clientId) {
        clientServices.getClientDataFromId(clientId)
          .then((response) => {
            setCode(response.code);
            setName(response.name);
            setContactPersonName(response.contactPersonName);
            setMobileNo(response.mobileNo);
            setEmailId(response.emailId);
            setAddress(response.address);

            const currentCategory = response.category || "ADHOC";
            setCategory(currentCategory);

            if (response.jobRates) {
              const mergedRates = getInitializedRates(currentCategory, response.jobRates);
              setJobRates(mergedRates);
            } else {
              setJobRates(getInitializedRates(currentCategory, []));
            }

            setLoading(false);
          })
          .catch((error) => {
            setError(error.message);
            setTimeout(() => {
              setError(null);
            }, 5000);
            setLoading(false);
          });
      }
    }
  }, [accessToken, clientId]);

  const handleRateChange = (jobType, value) => {
    const updatedRates = jobRates.map(item => {
      if (item.jobType === jobType) {
        return { ...item, rate: value };
      }
      return item;
    });
    setJobRates(updatedRates);
    if (!loading) {
      setIsDirty(true);
    }
  };

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setCategory(newCategory);
    setJobRates(getInitializedRates(newCategory, []));
    if (!loading) {
      setIsDirty(true);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const ratesToSave = jobRates
      .filter(r => r.rate !== '' && !isNaN(r.rate) && parseFloat(r.rate) > 0)
      .map(r => ({
        ...r,
        rate: parseFloat(r.rate)
      }));

    const formData = {
      id,
      name,
      code,
      contactPersonName,
      mobileNo,
      emailId,
      address,
      category,
      jobRates: ratesToSave
    };

    if (accessToken) {
      if (clientId) {
        clientServices.updateClientData(formData)
          .then((updatedClient) => {
            localStorage.setItem("successMessage", "Client updated successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });

      } else {
        clientServices.postClientData(formData)
          .then((newClient) => {
            localStorage.setItem("successMessage", "Client added successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response.status === 337) {
              setSnackbarMessage(error.response.data);
            } else if (error.response?.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      }
    }
  };

  const handleCancel = () => {
    handleBack();
  };

  const handleBack = () => {
    if (isDirty) {
      setShowBackConfirmation(true);
    } else {
      navigate(navigationUrl);
    }
  };

  const handleConfirmBack = () => {
    setShowBackConfirmation(false);
    navigate(navigationUrl);
  };

  return (
    <div className="main-form-container">
      <PageHeader
        title={clientId ? "Edit Client" : "Add Client"}
        left={
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        }
      />
      <form onSubmit={handleSubmit} className="form" onChange={() => { if (!loading) setIsDirty(true); }}>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Client"
              variant="outlined"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter Client Name"
              required
            />
          </Grid2>


          {clientId ? (
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                type="text"
                label={t('client.code')}
                variant="outlined"
                fullWidth
                required
                value={code}
                disabled
              />
            </Grid2>
          ) : (
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('client.code')}
                variant="outlined"
                fullWidth
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter Client Code"
                required
              />
            </Grid2>
          )}

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label="Contact Person"
              variant="outlined"
              fullWidth
              value={contactPersonName}
              onChange={(e) => setContactPersonName(e.target.value)}
              placeholder="Enter Contact Person Name"
              required
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('client.mobile_no')}
              variant="outlined"
              fullWidth
              type="number"
              value={mobileNo}
              onChange={(e) => setMobileNo(e.target.value)}
              placeholder="Enter Mobile Number"
              required
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('client.email')}
              variant="outlined"
              fullWidth
              value={emailId}
              onChange={(e) => setEmailId(e.target.value)}
              type="email"
              placeholder="Enter Client Email"
              required
            />

          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                onChange={handleCategoryChange}
                label="Category"
                fullWidth
                required
              >
                <MenuItem value="ADHOC">Adhoc</MenuItem>
                <MenuItem value="FTE">FTE</MenuItem>
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('client.address')}
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={4}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter Client Address"
              required
            />
          </Grid2>

          {isSuperAdmin && (
            <Grid2 size={{ xs: 12 }} sx={{ mt: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)',
                  border: '1px solid',
                  borderColor: '#e2e8f0',
                  borderRadius: 3
                }}
              >
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      Billing Rates
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Configure the default standard rates for {category === 'FTE' ? 'Full Time Equivalent' : 'Adhoc Services'}.
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: 'white', px: 1.5, py: 0.5, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: '0 2px 0.25rem rgba(0,0,0,0.02)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: 0.5 }}>
                      {category} MODE
                    </Typography>
                  </Box>
                </Box>

                <Grid2 container spacing={2}>
                  {jobRates.map((rateItem, index) => (
                    <Grid2 key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: 'white',
                        p: 1.3,
                        borderRadius: 3,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 0.25rem 0.75rem rgba(14, 165, 233, 0.08)',
                          transform: 'translateY(-2px)'
                        }
                      }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', flex: 1, pr: 1 }}>
                          {rateItem.jobType}
                        </Typography>
                        <TextField
                          type="number"
                          size="small"
                          value={rateItem.rate}
                          onChange={(e) => handleRateChange(rateItem.jobType, e.target.value)}
                          inputProps={{ min: 0, style: { textAlign: 'right', fontWeight: 700, color: '#0f172a' } }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <Typography variant="body2" sx={{ mr: 0.5, color: '#94a3b8', fontWeight: 700 }}>£</Typography>
                              ),
                            },
                          }}
                          sx={{
                            width: '6.25rem',
                            '& .MuiOutlinedInput-root': {
                              bgcolor: '#f8fafc',
                              borderRadius: '0.5rem',
                              transition: 'all 0.2s ease',
                              '& fieldset': { borderColor: 'transparent' },
                              '&:hover fieldset': { borderColor: '#cbd5e1' },
                              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                            }
                          }}
                        />
                      </Box>
                    </Grid2>
                  ))}
                </Grid2>

              </Paper>
            </Grid2>
          )}

          <Grid2 size={{ xs: 12 }} className="form-actions" sx={{ flexDirection: { xs: 'row !important', sm: 'row' }, gap: { xs: 2, sm: 0 }, '& > button': { flex: { xs: 1, sm: 'none' }, margin: { xs: '0 !important', sm: '' } } }}>
            {clientId ?
              (<Button type="submit" variant="contained" className="form-button"
                startIcon={<EditIcon />}>
                {t('client.update')}
              </Button>
              ) : (
                <Button type="submit" variant="contained" className="form-button"
                  startIcon={<SaveIcon />}>
                  {t('client.save')}
                </Button>
              )}
            <Button type="button" variant="contained" color="inherit" onClick={handleCancel} className="form-button-reset"
              startIcon={<CancelIcon />}>
              {t('client.cancel')}
            </Button>
          </Grid2>
        </Grid2>
      </form>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ whiteSpace: 'pre-line' }}>
          {Array.isArray(snackbarMessage) ? (
            snackbarMessage.map((msg, index) => (
              <div key={index}>{msg}</div>
            ))
          ) : (
            snackbarMessage
          )}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={showBackConfirmation}
        onClose={() => setShowBackConfirmation(false)}
        onConfirm={handleConfirmBack}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmLabel="Leave Page"
        confirmColor="error"
      />
    </div>
  );
};

export default ClientForm;
